// 警報處理時限（EVT-07 接手時限，SPEC #vms-web-alarm-workbench「處理時限 = 接手時限」）：組織預設＋節點覆寫，安防類 / 狀態類各一個分鐘數。
// 還沒有人接手的警報超過時限 → 畫面標逾時、通知被指派的人＋主管一次；逾時只提醒、不改狀態。owner / org_admin 能改，其他成員只看。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOrgAdmin, orgApi, type AlarmSlaRow, type MyOrg } from '../../lib/planeApi';
import { errText } from './OrgSection';

const MAX = { security: 1440, status: 10080 } as const;
type Group = keyof typeof MAX;

/** 輸入框的字 → 分鐘數（空白 = null）；不是 1–上限的整數 → undefined（不合法）。 */
export function parseMinutes(v: string, max: number): number | null | undefined {
  const s = v.trim();
  if (!s) return null;
  if (!/^\d+$/.test(s)) return undefined;
  const n = Number(s);
  return n >= 1 && n <= max ? n : undefined;
}

/** 節點覆寫的一格：沿用（null）/ 不設（0）/ 分鐘數。 */
type Mode = 'inherit' | 'off' | 'set';
const modeOf = (v: number | null | undefined): Mode => (v === null || v === undefined ? 'inherit' : v === 0 ? 'off' : 'set');

export function AlarmSlaCard({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const admin = isOrgAdmin(org);
  const q = useQuery({ queryKey: ['alarm-sla', org.org_id], queryFn: () => orgApi.alarmSla(org), retry: false });
  const sites = useQuery({ queryKey: ['sites', org.org_id], queryFn: () => orgApi.sites(org), staleTime: 60_000, retry: false });
  const reload = () => qc.invalidateQueries({ queryKey: ['alarm-sla', org.org_id] });
  // 舊平台沒有這支 API、或回應不是預期的形狀：不顯示（不讓整頁掛掉）
  if (q.isError || !q.data?.default || !Array.isArray(q.data.sites)) return null;
  const nameOf = (id: string) => sites.data?.find((s) => s.site_id === id)?.name ?? t('sla.node_unknown');
  const used = new Set(q.data.sites.map((s) => s.site_id));
  const free = (sites.data ?? []).filter((s) => s.parent_site_id && !used.has(s.site_id) && s.status === 'active');
  return (
    <section className="card" aria-label={t('sla.title')} data-testid="alarm-sla-card">
      <h2 className="section-title">{t('sla.title')}</h2>
      <p className="muted small">{t('sla.hint')}</p>
      <DefaultRow org={org} row={q.data.default} admin={admin} onSaved={reload} />
      <h3 className="small">{t('sla.nodes')}</h3>
      {q.data.sites.length === 0 && <p className="muted small">{t('sla.nodes_none')}</p>}
      <ul className="plain-list">
        {q.data.sites.map((r) => (
          <SiteRow key={r.site_id} org={org} row={r} name={nameOf(r.site_id ?? '')} admin={admin} onSaved={reload} />
        ))}
      </ul>
      {admin && free.length > 0 && <AddSite org={org} options={free.map((s) => ({ id: s.site_id, name: s.name }))} onSaved={reload} />}
    </section>
  );
}

function DefaultRow({ org, row, admin, onSaved }: { org: MyOrg; row: AlarmSlaRow; admin: boolean; onSaved(): void }) {
  const { t } = useTranslation();
  const [v, setV] = useState({ security: row.security_minutes?.toString() ?? '', status: row.status_minutes?.toString() ?? '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const parsed = { security: parseMinutes(v.security, MAX.security), status: parseMinutes(v.status, MAX.status) };
  const bad = (['security', 'status'] as Group[]).filter((g) => parsed[g] === undefined);
  const dirty = (parsed.security ?? null) !== row.security_minutes || (parsed.status ?? null) !== row.status_minutes;
  const save = async () => {
    if (bad.length) return;
    setBusy(true);
    setMsg(null);
    try {
      await orgApi.putAlarmSla(org, { security_minutes: parsed.security ?? null, status_minutes: parsed.status ?? null, expected_version: row.version });
      setMsg({ ok: true, text: t('policy.saved') });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row wrap" data-testid="alarm-sla-default">
      <strong className="small">{t('sla.default')}</strong>
      {(['security', 'status'] as Group[]).map((g) => (
        <label key={g} className="small">
          {t(`sla.${g}`)}{' '}
          <input
            className="input input-sm sla-min"
            inputMode="numeric"
            value={v[g]}
            disabled={!admin || busy}
            placeholder={t('sla.off')}
            aria-invalid={parsed[g] === undefined}
            data-sla-field={g}
            onChange={(e) => setV((x) => ({ ...x, [g]: e.target.value }))}
          />{' '}
          {t('sla.minutes')}
        </label>
      ))}
      {admin && (
        <button type="button" className="btn btn-sm btn-primary" disabled={busy || !dirty || bad.length > 0} onClick={() => void save()} data-testid="alarm-sla-save">
          {busy ? t('common.saving') : t('common.save')}
        </button>
      )}
      {bad.length > 0 && <span className="error small">{t('sla.invalid', { max: MAX[bad[0]] })}</span>}
      {msg && <span className={msg.ok ? 'muted small' : 'error small'}>{msg.text}</span>}
    </div>
  );
}

function ModeInput({ g, mode, value, disabled, onMode, onValue }: { g: Group; mode: Mode; value: string; disabled: boolean; onMode(m: Mode): void; onValue(v: string): void }) {
  const { t } = useTranslation();
  return (
    <label className="small">
      {t(`sla.${g}`)}{' '}
      <select className="input input-sm" value={mode} disabled={disabled} onChange={(e) => onMode(e.target.value as Mode)} data-sla-mode={g}>
        <option value="inherit">{t('sla.inherit')}</option>
        <option value="set">{t('sla.set')}</option>
        <option value="off">{t('sla.node_off')}</option>
      </select>
      {mode === 'set' && (
        <>
          {' '}
          <input className="input input-sm sla-min" inputMode="numeric" value={value} disabled={disabled} onChange={(e) => onValue(e.target.value)} data-sla-field={g} /> {t('sla.minutes')}
        </>
      )}
    </label>
  );
}

function siteBody(mode: Record<Group, Mode>, val: Record<Group, string>): { security_minutes: number | null; status_minutes: number | null } | null {
  const out: Record<Group, number | null> = { security: null, status: null };
  for (const g of ['security', 'status'] as Group[]) {
    if (mode[g] === 'off') out[g] = 0;
    else if (mode[g] === 'set') {
      const n = parseMinutes(val[g], MAX[g]);
      if (n === undefined || n === null) return null;
      out[g] = n;
    }
  }
  return { security_minutes: out.security, status_minutes: out.status };
}

function SiteRow({ org, row, name, admin, onSaved }: { org: MyOrg; row: AlarmSlaRow; name: string; admin: boolean; onSaved(): void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Record<Group, Mode>>({ security: modeOf(row.security_minutes), status: modeOf(row.status_minutes) });
  const [val, setVal] = useState<Record<Group, string>>({
    security: row.security_minutes ? String(row.security_minutes) : '',
    status: row.status_minutes ? String(row.status_minutes) : '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const body = siteBody(mode, val);
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setMsg(null);
    try {
      await f();
      setMsg({ ok: true, text: t('policy.saved') });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: errText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="row wrap" data-sla-site={row.site_id}>
      <strong className="small">{name}</strong>
      {(['security', 'status'] as Group[]).map((g) => (
        <ModeInput key={g} g={g} mode={mode[g]} value={val[g]} disabled={!admin || busy} onMode={(m) => setMode((x) => ({ ...x, [g]: m }))} onValue={(v) => setVal((x) => ({ ...x, [g]: v }))} />
      ))}
      {admin && (
        <>
          <button type="button" className="btn btn-sm btn-primary" disabled={busy || !body} onClick={() => void run(() => orgApi.putAlarmSlaSite(org, row.site_id!, { ...body!, expected_version: row.version }))}>
            {t('common.save')}
          </button>
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void run(() => orgApi.deleteAlarmSlaSite(org, row.site_id!, row.version))} data-testid="alarm-sla-site-reset">
            {t('sla.reset')}
          </button>
        </>
      )}
      {!body && <span className="error small">{t('sla.invalid_node')}</span>}
      {msg && <span className={msg.ok ? 'muted small' : 'error small'}>{msg.text}</span>}
    </li>
  );
}

function AddSite({ org, options, onSaved }: { org: MyOrg; options: { id: string; name: string }[]; onSaved(): void }) {
  const { t } = useTranslation();
  const [site, setSite] = useState('');
  const [mode, setMode] = useState<Record<Group, Mode>>({ security: 'set', status: 'inherit' });
  const [val, setVal] = useState<Record<Group, string>>({ security: '', status: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const body = siteBody(mode, val);
  const add = async () => {
    if (!site || !body) return;
    setBusy(true);
    setErr(null);
    try {
      await orgApi.putAlarmSlaSite(org, site, { ...body, expected_version: 0 });
      setSite('');
      setVal({ security: '', status: '' });
      onSaved();
    } catch (e) {
      setErr(errText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row wrap" data-testid="alarm-sla-add">
      <select className="input input-sm" value={site} onChange={(e) => setSite(e.target.value)} aria-label={t('sla.pick_node')}>
        <option value="">{t('sla.pick_node')}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      {(['security', 'status'] as Group[]).map((g) => (
        <ModeInput key={g} g={g} mode={mode[g]} value={val[g]} disabled={busy} onMode={(m) => setMode((x) => ({ ...x, [g]: m }))} onValue={(v) => setVal((x) => ({ ...x, [g]: v }))} />
      ))}
      <button type="button" className="btn btn-sm" disabled={busy || !site || !body} onClick={() => void add()}>
        {t('sla.add')}
      </button>
      {err && <span className="error small">{err}</span>}
    </div>
  );
}
